import React from 'react';
import { reaction, observable } from 'mobx';
import { observer } from 'mobx-react';
import PropTypes from 'prop-types';
import Styled from 'styled';

import LoadingProgress from 'lib/react-router/loading-progress';
import FetchData from 'lib/fetch-data/fetch-data';
import { Halfpage } from 'lib/google/dfp/ad-units/halfpage';
import { Targetting } from 'lib/google/dfp/google-dfp';

import { CategoryStore } from 'four-nets/forum/forum';
import PopularThreads from 'four-nets/forum/popular-threads/popular-threads';
import ErrorPage from 'four-nets/errors/error-page';
import FeaturedContent from 'four-nets/featured-content/featured-content';
import { ForumPromoWidgetView, ForumPromoWidgetViewModel } from 'four-nets/forum/forum-widget/forum-widget-view';
import ThreadsTableView, { ThreadsTableViewModel } from 'four-nets/forum/threads-table/threads-table-view';

import PromoPosts from './category-promo/promo-posts';
import PromoBox from './category-promo/promo-box';
import defaultStyles from './category-threads-loader.scss';

@observer
class CategoryThreads extends React.Component {
    @observable threadsTableViewModel;

    update = () => {
        this.threadsTableViewModel.setAscending(this.props.asc);
        this.threadsTableViewModel.setThreads(this.props.threads);
    };

    constructor(props) {
        super(props);

        this.threadsTableViewModel = new ThreadsTableViewModel();
        this.update();
    }

    componentDidMount() {
        this.disposer = reaction(() => this.props.threads, this.update);
    }

    componentWillUnmount() {
        if (this.disposer) {
            this.disposer();
        }
    }

    render() {
        const { category, loading } = this.props;
        return (
            <ThreadsTableView
                category={category}
                loading={loading}
                threadsTableViewModel={this.threadsTableViewModel}
            />
        );
    }
}

class CategoryThreadsLoader extends React.PureComponent {
    static contextTypes = {
        categoryStore: PropTypes.instanceOf(CategoryStore),
    };

    constructor(props, context) {
        super(props, context);
        this.dataSource = {
            categoryStore: this.context.categoryStore,
        };

        this.forumPromoWidgetViewModel = new ForumPromoWidgetViewModel(this.dataSource);
    }

    transform = data => {
        const { categoryStore } = this.context;
        const { isTargetedPromoCampaign } = this.props;

        if (isTargetedPromoCampaign) {
            this.forumPromoWidgetViewModel.fromJSON(data.forumPromoWidgetViewModel);
        }

        let threads = null;
        if (data.threads) {
            threads = categoryStore.threadStore.processPagedJSON({ json: data.threads });
        }

        return {
            ...data,
            threads,
            forumPromoWidgetViewModel: this.forumPromoWidgetViewModel,
        };
    };

    fetch = (setData, { page, asc }) => {
        const { category, isTargetedPromoCampaign } = this.props;

        const promises = [];
        if (category) {
            promises.push(category.loadThreads({ page, asc }));
        } else {
            promises.push(Promise.resolve(null));
        }

        if (isTargetedPromoCampaign) {
            promises.push(this.forumPromoWidgetViewModel.load());
        } else {
            promises.push(Promise.resolve(null));
        }

        return Promise.all(promises).then(([threads]) => {
            setData({
                page,
                asc,
                threads,
                forumPromoWidgetViewModel: this.forumPromoWidgetViewModel,
            });
        });
    };

    renderFetch = ({ data, error, loading }) => {
        const { styles, category, isTargetedPromoCampaign, match } = this.props;
        const { slug } = match.params;

        return (
            <LoadingProgress loading={loading}>
                <ErrorPage error={error}>
                    <div className={styles.wrapper}>
                        {data ? (
                            <div className={styles.outterWrapper}>
                                {isTargetedPromoCampaign ? <PromoBox /> : null}
                                <CategoryThreads
                                    category={category}
                                    threads={data.threads}
                                    loading={loading}
                                    page={data.page}
                                    asc={data.asc}
                                />
                            </div>
                        ) : (
                            <div className={styles.skeleton} />
                        )}
                        <div>
                            {isTargetedPromoCampaign ? (
                                <>
                                    <PromoPosts />
                                    <ForumPromoWidgetView
                                        style={{ promoWidget: styles.promoWidget }}
                                        forumPromoWidgetViewModel={
                                            data?.forumPromoWidgetViewModel || this.forumPromoWidgetViewModel
                                        }
                                    />
                                </>
                            ) : (
                                <React.Fragment>
                                    {category?.name ? (
                                        <PopularThreads category={category?.name} categorySlug={slug} />
                                    ) : null}
                                    <div className={styles.adWrapper}>
                                        <Halfpage
                                            targettings={[new Targetting('f-rectangle-cat-slug', [category?.slug])]}
                                            adUnitCode='halfpage-top'
                                        />
                                    </div>
                                    <FeaturedContent />
                                </React.Fragment>
                            )}
                        </div>
                    </div>
                </ErrorPage>
            </LoadingProgress>
        );
    };

    render() {
        const { category, search } = this.props;
        const { page = 1, asc = false } = search;

        return (
            <FetchData
                serializerKey={__filename}
                transform={this.transform}
                render={this.renderFetch}
                fetch={this.fetch}
                category={category}
                page={page}
                asc={asc}
            />
        );
    }
}

export default Styled(defaultStyles)(CategoryThreadsLoader);
